import React, { useMemo } from "react";
import { ColorValue, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import MarkdownView from "../components/MarkdownView";
import IconButton from "../components/IconButton";
import Icon, { IconName } from "../components/Icon";
import Gap from "./Gap";

export type CalloutMode = "info" | "warning" | "error" | "success" | "secondary";

export type CalloutProps = {
    markdown: string;
    mode?: CalloutMode;
    style?: ViewStyle;
    onLinkPress?: (link: string) => boolean;
    onClose?: () => void;
    testID?: string;
    icon?: IconName;
};

const Callout: React.FC<CalloutProps> = ({
    markdown,
    mode,
    style,
    onLinkPress,
    testID,
    onClose,
    icon,
}) => {
    const { colors } = useTheme();
    const styles = useStylesheet();
    const background = useMemo(() => {
        switch (mode) {
            case "warning":
                return colors.warningBackground;
            case "error":
                return colors.errorBackground;
            case "info":
                return colors.infoBackground;
            case "success":
                return colors.successBackground;
            case "secondary":
                return colors.secondaryContainer;
            default:
                return colors.infoBackground;
        }
    }, [colors, mode]);
    const onBackground = useMemo(() => {
        switch (mode) {
            case "warning":
                return colors.onWarningBackground;
            case "error":
                return colors.onErrorBackground;
            case "info":
                return colors.onInfoBackground;
            case "success":
                return colors.onSuccessBackground;
            case "secondary":
                return colors.secondary;
            default:
                return colors.onInfoBackground;
        }
    }, [colors, mode]);

    const markdownStyle = useCalloutMarkdownStyle({ fontColor: onBackground });

    return (
        <View testID={testID} style={[styles.callout, { backgroundColor: background }, style]}>
            <Gap grow />
            {icon && <Icon name={icon} style={styles.leftIcon} size={14} color={onBackground} />}
            <MarkdownView
                markdown={markdown}
                markdownStyle={markdownStyle}
                onLinkPress={onLinkPress}
                fontColor={onBackground}
                style={{ flexShrink: 1 }}
            />
            <Gap grow />
            {onClose && (
                <IconButton icon="close" onPress={onClose} size={16} iconColor={onBackground} />
            )}
        </View>
    );
};

export const useCalloutMarkdownStyle = ({ fontColor }: { fontColor: ColorValue }) => {
    const { fonts, gridSize, colors } = useTheme();
    return useMemo(() => {
        return StyleSheet.create({
            // The main container

            // @pseudo class, does not have a unique render rule
            heading1: {
                ...fonts.titleLarge,
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                fontSize: 30,
            },
            heading2: {
                ...fonts.titleMedium,
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                fontSize: 18,
            },
            heading3: {
                ...fonts.titleSmall,
                fontWeight: "bold",
                marginBottom: gridSize / 4,
                color: fontColor,
                fontSize: 15,
            },
            heading4: {
                ...fonts.bodyLarge,
                color: fontColor,
                marginBottom: gridSize / 4,
                fontSize: 14,
            },
            heading5: {
                ...fonts.bodyMedium,
                color: fontColor,
                marginBottom: gridSize / 4,
                fontSize: 14,
            },
            heading6: {
                ...fonts.bodySmall,
                color: fontColor,
                marginBottom: gridSize / 4,
                fontSize: 11,
            },
            bullet_list: {
                ...fonts.bodyMedium,
                color: fontColor,
                lineHeight: 20, // same as normal small line height
                fontSize: 14,
            },
            ordered_list: {
                ...fonts.bodyMedium,
                color: fontColor,
                lineHeight: 20, // same as normal small line height
                fontSize: 14,
            },
            paragraph: {
                ...fonts.bodyMedium,
                marginTop: 0,
                marginBottom: 0,
                color: fontColor,
                fontSize: 14,
                lineHeight: 20,
            },
            bold: {
                ...fonts.bodyLarge,
                fontSize: 14,
                lineHeight: 20,
                color: fontColor,
            },
            strong: {
                ...fonts.bodyLarge,
                fontSize: 14,
                lineHeight: 20,
                color: fontColor,
            },
            bullet_list_icon: {
                marginLeft: 0,
                marginRight: 10,
            },
            hardbreak: {
                width: "100%",
                height: 1,
            },
            link: {
                color: colors.secondary,
            },
            blocklink: {
                color: colors.secondary,
            },
            helpButton: {
                margin: 0,
                marginHorizontal: 4,
                borderColor: colors.warningBorder,
                borderWidth: 1,
                backgroundColor: colors.warningBackground,
            },
        });
    }, [fontColor, fonts, colors, gridSize]);
};

const useStylesheet = () => {
    const theme = useTheme();
    return React.useMemo(() => {
        return StyleSheet.create({
            callout: {
                alignSelf: "stretch",
                paddingTop: 4,
                paddingBottom: 4,
                paddingHorizontal: theme.gridSize / 2,
                borderRadius: theme.roundness,
                flexDirection: "row",
                alignItems: "center",
            },
            leftIcon: {
                marginRight: 6,
            },
        });
    }, [theme]);
};

export default Callout;
